import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { EventqDetectionBoundingBox } from '../../primitives/detection-bounding-box/detection-bounding-box';
import type {
  EventqDetectionBox,
  EventqDetectionBoxClick,
  EventqDetectionBoundingBoxActivate,
  EventqDetectionEventType,
} from '../../primitives/detection-bounding-box/detection-bounding-box.types';
import {
  mapDetectionBoxesToContainer,
  type EventqVideoContentLayout,
} from '../../utils/video-content-layout.util';

export type {
  EventqDetectionBox,
  EventqDetectionBoxClick,
  EventqDetectionEventType,
} from '../../primitives/detection-bounding-box/detection-bounding-box.types';

export type { EventqVideoContentLayout } from '../../utils/video-content-layout.util';

@Component({
  selector: 'eventq-detection-overlay',
  imports: [EventqDetectionBoundingBox],
  templateUrl: './detection-overlay.html',
  styleUrl: './detection-overlay.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventqDetectionOverlay {
  readonly boxes = input<EventqDetectionBox[]>([]);
  readonly visibleTypes = input<EventqDetectionEventType[]>([]);
  readonly activeOffsetSeconds = input<number | null>(null);
  readonly clickable = input(false);
  readonly animated = input(true);
  /** When set, maps box coordinates from video-frame space to the overlay stage. */
  readonly videoLayout = input<EventqVideoContentLayout | null>(null);

  readonly boxClick = output<EventqDetectionBoxClick>();

  readonly visibleBoxes = computed(() => {
    const types = this.visibleTypes();
    let items = this.boxes();
    if (types.length) {
      const allowed = new Set(types);
      items = items.filter((box) => allowed.has(box.eventType));
    }
    const activeTime = this.activeOffsetSeconds();
    if (activeTime !== null) {
      const second = Math.floor(activeTime);
      items = items.filter((box) => Math.floor(box.offsetSeconds ?? 0) === second);
    }
    return items;
  });

  readonly stageBoxes = computed(() =>
    mapDetectionBoxesToContainer(this.visibleBoxes(), this.videoLayout()),
  );

  onBoxClick(box: EventqDetectionBox, event: EventqDetectionBoundingBoxActivate): void {
    if (!this.clickable()) {
      return;
    }
    this.boxClick.emit({ box, anchorElement: event.anchorElement });
  }
}
